import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormGroup, FormControl, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { filter, Subject, take, takeUntil } from 'rxjs'
import { CompanyAPIModel } from 'src/app/models/api.model'
import { AuthType } from 'src/app/models/model'
import { CommonService } from 'src/app/services/common/common.service'
import { resetCompany, save_others } from 'src/app/store/action/onboarding.action'
import { getCompany } from 'src/app/store/selector/onboarding.selector'
import Swal from 'sweetalert2'

@Component({
  selector: 'app-other-company-settings',
  templateUrl: './other-company-settings.component.html',
  styleUrls: ['./other-company-settings.component.scss']
})
export class OtherCompanySettingsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>()

  loginMechanismForm!: FormGroup
  mailSettingsForm!: FormGroup
  companyDomainForm!: FormGroup
  ssoForm!: FormGroup

  companyGroupId!: string

  constructor(private readonly router: Router, private readonly store: Store, private readonly cs: CommonService) {}

  ngOnInit(): void {
    this.buildForms()
    this.handleLoginMechanismToggle()
    this.handleSsoValidation()

    this.store
      .select(getCompany)
      .pipe(filter(Boolean), take(1), takeUntil(this.destroy$))
      .subscribe(company => this.patchValues(company))
  }

  private buildForms(): void {
    this.loginMechanismForm = new FormGroup({
      passwordLogin: new FormControl(false),
      otpLogin: new FormControl(false),
      ssoLogin: new FormControl(false)
    })

    this.mailSettingsForm = new FormGroup({
      triggerEmployeeWelcomeEmail: new FormControl(false),
      triggerAdminWelcomeEmail: new FormControl(false),
      employeeAndEeAdminNotificationsEnabled: new FormControl({ value: false, disabled: true })
    })

    this.ssoForm = new FormGroup({
      tenantId: new FormControl(''),
      clientId: new FormControl(''),
      ssoProvider: new FormControl('MICROSOFT')
    })

    this.companyDomainForm = new FormGroup({
      companyDomain: new FormControl('', [Validators.required])
    })
  }

  /**
   * Ensures ONLY ONE login mechanism stays ON at a time
   * Others are automatically turned OFF
   */
  private handleLoginMechanismToggle(): void {
    this.loginMechanismForm
      .get('passwordLogin')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(checked => {
        if (checked) {
          this.loginMechanismForm.patchValue({ otpLogin: false, ssoLogin: false }, { emitEvent: false })
        }
      })

    this.loginMechanismForm
      .get('otpLogin')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(checked => {
        if (checked) {
          this.loginMechanismForm.patchValue({ passwordLogin: false, ssoLogin: false }, { emitEvent: false })
        }
      })

    this.loginMechanismForm
      .get('ssoLogin')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(checked => {
        if (checked) {
          this.loginMechanismForm.patchValue({ passwordLogin: false, otpLogin: false }, { emitEvent: false })
        }
      })
  }

  /**
   * Makes SSO fields mandatory ONLY when SSO is ON
   */
  private handleSsoValidation(): void {
    this.loginMechanismForm
      .get('ssoLogin')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(isChecked => {
        const tenantId = this.ssoForm.get('tenantId')
        const clientId = this.ssoForm.get('clientId')

        if (isChecked) {
          tenantId?.setValidators([Validators.required])
          clientId?.setValidators([Validators.required])
        } else {
          this.ssoForm.reset()
          tenantId?.clearValidators()
          clientId?.clearValidators()
        }

        tenantId?.updateValueAndValidity()
        clientId?.updateValueAndValidity()
      })
  }

  private patchValues(company: CompanyAPIModel): void {
    this.companyGroupId = company.id

    const details = company?.additionalDetails
    if (!details) return

    if (details.authType === AuthType.PASSWORD) {
      this.loginMechanismForm.patchValue({ passwordLogin: true })
    }

    if (details.authType === AuthType.OTP) {
      this.loginMechanismForm.patchValue({ otpLogin: true })
    }

    if (details.authType === AuthType.SSO) {
      this.loginMechanismForm.patchValue({ ssoLogin: true })
      this.ssoForm.patchValue({
        tenantId: details.tenantId,
        clientId: details.clientId,
        ssoProvider: 'MICROSOFT'
      })
    }

    this.mailSettingsForm.patchValue({
      triggerEmployeeWelcomeEmail: details.triggerEmployeeWelcomeEmail,
      triggerAdminWelcomeEmail: details.triggerAdminWelcomeEmail,
      employeeAndEeAdminNotificationsEnabled: details.employeeAndEeAdminNotificationsEnabled ?? true
    })

    // this.companyDomainForm.patchValue({
    //   companyDomain: details.companyDomain
    // })
  }

  private getSelectedAuthType(): AuthType | null {
    const value = this.loginMechanismForm.value

    if (value.passwordLogin) return AuthType.PASSWORD
    if (value.otpLogin) return AuthType.OTP
    if (value.ssoLogin) return AuthType.SSO

    return null
  }

  discard(): void {
    this.router.navigate(['/settings'])
  }

  finish(): void {
    const authType = this.getSelectedAuthType()

    if (!authType) {
      this.cs.showError('Please select a login mechanism.')
      return
    }

    if (authType === AuthType.SSO && this.ssoForm.invalid) {
      this.ssoForm.markAllAsTouched()
      this.cs.showError('All SSO fields are mandatory.')
      return
    }

    if (!this.companyGroupId) {
      this.cs.showError('Company Group ID is missing.')
      return
    }

    if (this.companyDomainForm.invalid) {
      this.cs.showError('Enter Company Domain.')
      return
    }

    Swal.fire({
      title: 'Are you sure you want to save the changes?',
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: 'Yes',
      denyButtonText: 'No'
    }).then(result => {
      if (result.isConfirmed) {
        const body = {
          ...this.mailSettingsForm.getRawValue(),
          ...this.companyDomainForm.value,
          ...(authType === AuthType.SSO ? this.ssoForm.value : {}),
          authType
        }

        this.store.dispatch(
          save_others({
            companyGroupId: this.companyGroupId,
            others: body,
            completeOnboarding: true
          })
        )
      }
    })
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetCompany())

    this.destroy$.next()
    this.destroy$.complete()

    this.loginMechanismForm?.reset()
    this.mailSettingsForm?.reset()
    this.ssoForm?.reset()
    this.companyDomainForm?.reset()

    this.companyGroupId = ''
  }
}
